import React from 'react';
import {
  Checkbox,
  Divider,
  Grid,
  Icon,
  Input,
  Label,
  CheckboxProps
} from 'semantic-ui-react';
import { SectionTitle } from '../../styles';
import { FieldLabel } from '../../../../styles';
import { warningNotification } from '../../../../services/notification';
import { isValidEmail } from '../../../../utils/emailValidator';
import IconButton from '../../../../components/IconButton';
import { get } from '../../../../services/storage';
import { isBackofficeTestUser } from '../../../../util';

interface FormData {
  creationResponsible: boolean;
  client: boolean;
  estateManager: boolean;
  estateMain: boolean;
  tempMail: string;
  additionalRecipient: string[];
}

interface RenderSectionProps {
  title: string;
  sectionKey: string;
  formData: FormData;
  onUpdate: (sectionKey: string, updatedData: FormData) => void;
}

const RenderSection = ({ title, sectionKey, formData, onUpdate }: RenderSectionProps) => {
  const isBackofficeTest = isBackofficeTestUser({ role: get('role') || undefined });

  const handleCheckboxChange =
    (field: string) =>
    (_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
      if (isBackofficeTest) return;

      const { checked } = data;
      if (checked !== undefined) {
        onUpdate(sectionKey, {
          ...formData,
          [field]: Boolean(checked)
        });
      }
    };

  const handleInputChange =
    (field: string) =>
    (_event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      if (isBackofficeTest) return;

      const { value } = data;
      onUpdate(sectionKey, {
        ...formData,
        [field]: value
      });
    };

  const handleAddRecipient = () => {
    if (isBackofficeTest) return;

    const email = formData.tempMail.trim();

    if (!isValidEmail(email)) {
      warningNotification('Email inválido', 'Por favor, insira um email válido.');
      return;
    }

    if (
      formData.additionalRecipient.some(
        existingEmail => existingEmail.toLowerCase() === email.toLowerCase()
      )
    ) {
      warningNotification('Email duplicado', 'Este email já foi adicionado.');
      return;
    }

    onUpdate(sectionKey, {
      ...formData,
      additionalRecipient: [...formData.additionalRecipient, email],
      tempMail: ''
    });
  };

  const handleRemoveRecipient = (email: string) => () => {
    if (isBackofficeTest) return;

    onUpdate(sectionKey, {
      ...formData,
      additionalRecipient: formData.additionalRecipient.filter(mail => mail !== email)
    });
  };

  return (
    <>
      <SectionTitle>
        <IconButton name="mail" /> {title}
      </SectionTitle>
      <Divider />
      <Grid>
        <Grid.Row>
          <Grid.Column style={{ width: 208 }}>
            <Checkbox
              id={`${sectionKey}-creationResponsible`}
              label="Responsável pela criação"
              checked={formData.creationResponsible}
              disabled={isBackofficeTest}
              onChange={handleCheckboxChange('creationResponsible')}
            />
          </Grid.Column>
          <Grid.Column width={2}>
            <Checkbox
              id={`${sectionKey}-client`}
              label="Cliente"
              checked={formData.client}
              disabled={isBackofficeTest}
              onChange={handleCheckboxChange('client')}
            />
          </Grid.Column>
          <Grid.Column style={{ width: 120 }}>
            <Checkbox
              id={`${sectionKey}-estateMain`}
              label="Imobiliária"
              checked={formData.estateMain}
              disabled={isBackofficeTest}
              onChange={handleCheckboxChange('estateMain')}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <Checkbox
              id={`${sectionKey}-estateManager`}
              label="Gestor de Contas"
              checked={formData.estateManager}
              disabled={isBackofficeTest}
              onChange={handleCheckboxChange('estateManager')}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={14}>
            <FieldLabel>Adicionar destinatário (Opcional)</FieldLabel>
            <Input
              placeholder="Digite o email do destinatário..."
              fluid
              value={formData.tempMail}
              disabled={isBackofficeTest}
              onChange={handleInputChange('tempMail')}
            />
          </Grid.Column>
          <Grid.Column width={1}>
            <IconButton
              name="plus square"
              style={{
                cursor: 'pointer',
                fontSize: 40,
                marginTop: 30,
                opacity: isBackofficeTest ? 0.5 : 1
              }}
              disabled={isBackofficeTest}
              onClick={handleAddRecipient}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ marginTop: -40 }}>
          <Grid.Column>
            {formData.additionalRecipient.length > 0 && (
              <div style={{ marginTop: 0 }}>
                {formData.additionalRecipient.map((email, index) => (
                  <Label key={index} size="large" style={{ margin: '5px 5px' }}>
                    {email}
                    <Icon
                      name="delete"
                      style={{
                        cursor: isBackofficeTest ? 'default' : 'pointer',
                        marginLeft: 10,
                        opacity: isBackofficeTest ? 0.5 : 1
                      }}
                      onClick={isBackofficeTest ? undefined : handleRemoveRecipient(email)}
                    />
                  </Label>
                ))}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default RenderSection;
